import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useAuthStore } from '../store/auth.store';
import { registrarErroDeTela, tenantIdDoToken } from '../lib/log-erro-tela';
import { SUPER_ADMIN_NAV, TENANT_NAV } from '../lib/nav-config';
import { Button } from './ui';
import { cn } from './ui/cn';

/**
 * [AB#1030] Error Boundary compartilhado — um erro de render numa tela não derruba o app inteiro.
 *
 * Duas peças:
 *  - `ErrorBoundary` (classe — só classes pegam erro de render): genérico, sem conhecer rota/usuário.
 *    Reseta quando `resetKey` muda (o chamador passa o `pathname`) ou pelo `reset()` do fallback.
 *  - `ErrorBoundaryDeRota` (função): liga o genérico à rota atual + sessão (log estruturado, reset ao
 *    trocar de rota, fallback padrão). É ESTE que as telas/layouts usam.
 *
 * Onde fica: no `<Outlet/>` do `AppShell` (menu, header e breadcrumb ficam FORA e continuam
 * funcionando) e, como rede de segurança, em volta do `<Routes>` do `App` (telas sem shell:
 * login etc., variante `pagina`). Não usa `errorElement` do React Router porque o app roda em
 * `<BrowserRouter>` + `<Routes>` (não em data router, que é o que habilita `errorElement`).
 *
 * Também pega falha de chunk de `React.lazy` (deploy novo com o usuário na aba velha) — cai no mesmo
 * fallback e "Tentar novamente" refaz o import.
 */
interface ErrorBoundaryProps {
  children: ReactNode;
  /** Muda → o erro é limpo e os filhos tentam renderizar de novo (ex.: troca de rota). */
  resetKey?: string;
  onError?: (erro: Error, info: ErrorInfo) => void;
  renderFallback: (args: { reset: () => void }) => ReactNode;
}

interface ErrorBoundaryState {
  erro: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { erro: null };

  static getDerivedStateFromError(erro: Error): ErrorBoundaryState {
    return { erro };
  }

  componentDidCatch(erro: Error, info: ErrorInfo): void {
    this.props.onError?.(erro, info);
  }

  componentDidUpdate(prev: ErrorBoundaryProps): void {
    if (this.state.erro && prev.resetKey !== this.props.resetKey) this.reset();
  }

  reset = (): void => {
    this.setState({ erro: null });
  };

  render(): ReactNode {
    if (this.state.erro) return this.props.renderFallback({ reset: this.reset });
    return this.props.children;
  }
}

/** Rota "Início" da área em que o usuário está (admin × tenant) — mesmo `path` do 1º item do menu. */
function rotaInicio(pathname: string): string {
  const nav = pathname.startsWith('/admin') ? SUPER_ADMIN_NAV : TENANT_NAV;
  return nav[0]?.path ?? '/';
}

/**
 * Fallback padronizado. Sem stack trace, sem mensagem técnica — só texto genérico. `variante`:
 * `tela` (dentro do `<main>` do shell) ou `pagina` (tela cheia, fora do shell).
 */
export function FallbackErroTela({
  reset,
  variante = 'tela',
}: {
  reset: () => void;
  variante?: 'tela' | 'pagina';
}): JSX.Element {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  function voltarAoInicio(): void {
    reset(); // se já está na rota "início", a URL não muda → o reset por rota não dispararia
    navigate(rotaInicio(pathname));
  }
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 text-center px-6',
        variante === 'pagina' ? 'min-h-screen bg-bg-app' : 'py-16',
      )}
    >
      <div className="w-16 h-16 rounded-xl bg-bg-muted grid place-items-center text-text-faint-v2">
        <IconAlertTriangle size={28} aria-hidden="true" />
      </div>
      <h2 className="font-display text-[15px] font-bold text-text-primary">
        Algo deu errado nesta tela
      </h2>
      <p className="text-text-tertiary max-w-[360px]">
        Isso não afetou o resto do sistema. Tente novamente; se continuar, volte ao início e avise o
        suporte.
      </p>
      <div className="mt-1 flex gap-2">
        <Button variant="primary" onClick={reset}>
          Tentar novamente
        </Button>
        <Button variant="ghost" onClick={voltarAoInicio}>
          Voltar ao início
        </Button>
      </div>
    </div>
  );
}

export function ErrorBoundaryDeRota({
  children,
  variante = 'tela',
}: {
  children: ReactNode;
  variante?: 'tela' | 'pagina';
}): JSX.Element {
  const { pathname } = useLocation();
  const usuarioId = useAuthStore((s) => s.usuario?.id ?? null);
  const accessToken = useAuthStore((s) => s.access_token);
  return (
    <ErrorBoundary
      resetKey={pathname}
      onError={(erro, info) =>
        registrarErroDeTela(erro, info.componentStack, {
          rota: pathname,
          usuario_id: usuarioId,
          tenant_id: tenantIdDoToken(accessToken),
        })
      }
      renderFallback={({ reset }) => <FallbackErroTela reset={reset} variante={variante} />}
    >
      {children}
    </ErrorBoundary>
  );
}
